import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/authContext";
import { getSriLankaToday, getSriLankaDaysAgo } from "../utils/dateUtils";
import FloatingNavigation from "../components/FloatingNavigation";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Line,
  AreaChart,
  Area,
  ComposedChart,
} from "recharts";
import {
  FaCalendarDay,
  FaCalendarAlt,
  FaListAlt,
  FaChartLine,
  FaUsers,
  FaDollarSign,
  FaTicketAlt,
  FaFilter,
  FaSync,
  FaCog,
} from "react-icons/fa";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [stats, setStats] = useState({
    today: { tickets: 0, total: 0, sales: 0 },
    last7: { tickets: 0, total: 0, sales: 0 },
    last30: { tickets: 0, total: 0, sales: 0 },
    all: { tickets: 0, total: 0, sales: 0 },
  });
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [filter, setFilter] = useState("last30");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState("all");
  const [sites, setSites] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [selectedCustomerType, setSelectedCustomerType] = useState("");
  const [selectedBookingType, setSelectedBookingType] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [agents, setAgents] = useState([]);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch sites first
        const sitesRes = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/sites/active`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSites(sitesRes.data.sites || []);

        // Fetch sales data from admin API (all sites or specific site)
        let salesUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/sales`;
        if (selectedSite !== "all") {
          salesUrl += `?siteId=${selectedSite}`;
        }

        const salesRes = await axios.get(salesUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSales(salesRes.data.sales || []);
        console.log('Sales data sample:', salesRes.data.sales?.slice(0, 3));

        // Fetch agents for filter from all sites
        const agentsRes = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/get-users?role=Agent`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setAgents(agentsRes.data.users || []);

        // Calculate statistics using Sri Lankan timezone
        let todayStats = { tickets: 0, total: 0, sales: 0 };
        let weekStats = { tickets: 0, total: 0, sales: 0 };
        let monthStats = { tickets: 0, total: 0, sales: 0 };
        let allStats = { tickets: 0, total: 0, sales: 0 };

        const todayStr = getSriLankaToday();
        const last7Str = getSriLankaDaysAgo(6);
        const last30Str = getSriLankaDaysAgo(29);

        // Generate time series data
        const dailyData = {};

        salesRes.data.sales.forEach((sale) => {
          const date = sale.date;
          allStats.tickets += sale.numberOfTickets;
          allStats.total += sale.totalPrice;
          allStats.sales += 1;

          // Daily aggregation for charts
          if (!dailyData[date]) {
            dailyData[date] = { date, tickets: 0, revenue: 0, sales: 0 };
          }
          dailyData[date].tickets += sale.numberOfTickets;
          dailyData[date].revenue += sale.totalPrice;
          dailyData[date].sales += 1;

          if (date === todayStr) {
            todayStats.tickets += sale.numberOfTickets;
            todayStats.total += sale.totalPrice;
            todayStats.sales += 1;
          }
          if (date >= last7Str) {
            weekStats.tickets += sale.numberOfTickets;
            weekStats.total += sale.totalPrice;
            weekStats.sales += 1;
          }
          if (date >= last30Str) {
            monthStats.tickets += sale.numberOfTickets;
            monthStats.total += sale.totalPrice;
            monthStats.sales += 1;
          }
        });

        setStats({
          today: todayStats,
          last7: weekStats,
          last30: monthStats,
          all: allStats,
        });

        // Convert daily data to array and sort
        const timeSeriesArray = Object.values(dailyData)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(-30); // Last 30 days for chart

        setTimeSeriesData(timeSeriesArray);
      } catch (err) {
        console.error("Failed to fetch data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, selectedSite]);

  // Apply filters to sales data
  useEffect(() => {
    let filtered = [...sales];

    // Date filter using Sri Lankan timezone
    const todayStr = getSriLankaToday();
    const last7Str = getSriLankaDaysAgo(6);
    const last30Str = getSriLankaDaysAgo(29);
    const last90Str = getSriLankaDaysAgo(89);
    const last180Str = getSriLankaDaysAgo(179);

    // Year start calculation
    const yearStart = new Date();
    yearStart.setMonth(0, 1); // January 1st
    const yearStartStr = yearStart.toISOString().split("T")[0];

    // Apply date filter
    if (filter === "today") {
      filtered = filtered.filter((sale) => sale.date === todayStr);
    } else if (filter === "last7") {
      filtered = filtered.filter((sale) => sale.date >= last7Str);
    } else if (filter === "last30") {
      filtered = filtered.filter((sale) => sale.date >= last30Str);
    } else if (filter === "last90") {
      filtered = filtered.filter((sale) => sale.date >= last90Str);
    } else if (filter === "last180") {
      filtered = filtered.filter((sale) => sale.date >= last180Str);
    } else if (filter === "year") {
      filtered = filtered.filter((sale) => sale.date >= yearStartStr);
    } else if (filter === "custom" && customStartDate && customEndDate) {
      filtered = filtered.filter(
        (sale) => sale.date >= customStartDate && sale.date <= customEndDate
      );
    }

    // Apply advanced filters
    if (selectedAgent) {
      filtered = filtered.filter((sale) => sale.salesAgent === selectedAgent);
    }
    if (selectedCustomerType) {
      filtered = filtered.filter(
        (sale) => sale.customerType === selectedCustomerType
      );
    }
    if (selectedBookingType) {
      filtered = filtered.filter(
        (sale) => sale.bookingType === selectedBookingType
      );
    }
    if (selectedPaymentMethod) {
      filtered = filtered.filter(
        (sale) => sale.paymentMethod === selectedPaymentMethod
      );
    }

    setFilteredSales(filtered);
  }, [
    sales,
    filter,
    customStartDate,
    customEndDate,
    selectedAgent,
    selectedCustomerType,
    selectedBookingType,
    selectedPaymentMethod,
  ]);

  const getPieData = (key) => {
    const countMap = {};
    filteredSales.forEach((sale) => {
      const type = sale[key] || "Unknown";
      if (!countMap[type]) countMap[type] = 0;
      countMap[type] += sale.numberOfTickets;
    });
    return Object.entries(countMap).map(([name, value]) => ({ name, value }));
  };

  const getAgentPerformanceData = () => {
    const agentMap = {};
    filteredSales.forEach((sale) => {
      const agent = sale.salesAgent || sale.agentName || 'Unknown Agent';
      if (!agentMap[agent]) {
        agentMap[agent] = { agent, tickets: 0, revenue: 0, sales: 0 };
      }
      agentMap[agent].tickets += parseInt(sale.numberOfTickets) || 0;
      agentMap[agent].revenue += parseFloat(sale.totalPrice) || 0;
      agentMap[agent].sales += 1;
    });
    
    const result = Object.values(agentMap)
      .filter(item => item.sales > 0)
      .sort((a, b) => b.revenue - a.revenue);
    
    console.log('Agent Performance Data:', result);
    return result;
  };

  const getHourlyDistribution = () => {
    const hourlyData = {};
    for (let i = 0; i < 24; i++) {
      hourlyData[i] = { hour: `${i}:00`, sales: 0, tickets: 0 };
    }

    filteredSales.forEach((sale) => {
      if (sale.time) {
        const hour = parseInt(sale.time.split(":")[0]);
        hourlyData[hour].sales += 1;
        hourlyData[hour].tickets += sale.numberOfTickets;
      }
    });

    return Object.values(hourlyData).filter((item) => item.sales > 0);
  };

  const clearAllFilters = () => {
    setFilter("last30");
    setSelectedSite("all");
    setSelectedAgent("");
    setSelectedCustomerType("");
    setSelectedBookingType("");
    setSelectedPaymentMethod("");
    setCustomStartDate("");
    setCustomEndDate("");
  };

  const pieColors = [
    "#3b82f6", // blue-500
    "#06b6d4", // cyan-500
    "#10b981", // emerald-500
    "#f59e0b", // amber-500
    "#ef4444", // red-500
    "#8b5cf6", // violet-500
    "#ec4899", // pink-500
    "#84cc16", // lime-500
  ];

  const buttons = [
    {
      label: "Admin Sales Overview",
      path: "/admin-sales-overview",
      icon: <FaListAlt />,
    },
    { label: "Add Guide", path: "/add-guide", icon: <FaUsers /> },
    { label: "Notes", path: "/manage-notes", icon: <FaListAlt /> },
    { label: "User Management", path: "/manage-users", icon: <FaUsers /> },
    { label: "Site Management", path: "/manage-sites", icon: <FaCog /> },
    {
      label: "Guide Analytics",
      path: "/guide-analytics",
      icon: <FaChartLine />,
    },
  ];

  const statCards = [
    {
      title: "Total Revenue",
      value: `LKR ${stats.all.total.toLocaleString()}`,
      subtitle: `${stats.all.sales} sales`,
      icon: <FaDollarSign />,
      color: "revenue",
    },
    {
      title: "Total Tickets",
      value: stats.all.tickets.toLocaleString(),
      subtitle: "All time",
      icon: <FaTicketAlt />,
      color: "tickets",
    },
    {
      title: "Last 30 Days",
      value: `LKR ${stats.last30.total.toLocaleString()}`,
      subtitle: `${stats.last30.tickets} tickets`,
      icon: <FaCalendarAlt />,
      color: "month",
    },
    {
      title: "Today",
      value: `${stats.today.tickets} tickets`,
      subtitle: `LKR ${stats.today.total.toLocaleString()}`,
      icon: <FaCalendarDay />,
      color: "today",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <FaSync className="animate-spin text-4xl text-blue-600" />
          <p className="text-gray-600 text-lg">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <FloatingNavigation />
      
      <div className="w-full max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6 sm:mb-8">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <FaChartLine className="text-white text-lg sm:text-xl" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                  <p className="text-sm sm:text-base text-gray-600">Comprehensive analytics and management</p>
                </div>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 w-full sm:w-auto"
              >
                <FaSync className="text-sm" />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {statCards.map((card, index) => (
            <div key={index} className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-600 mb-2 truncate">{card.title}</h3>
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 mb-1 break-words">{card.value}</p>
                  <p className="text-xs sm:text-sm text-gray-500 truncate">{card.subtitle}</p>
                </div>
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ml-2 ${
                  card.color === 'revenue' ? 'bg-green-100 text-green-600' :
                  card.color === 'tickets' ? 'bg-blue-100 text-blue-600' :
                  card.color === 'month' ? 'bg-purple-100 text-purple-600' :
                  'bg-orange-100 text-orange-600'
                }`}>
                  {card.icon}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6 sm:mb-8">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Quick Actions</h2>
          </div>
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {buttons.map((btn, idx) => (
                <button
                  key={idx}
                  onClick={() => navigate(btn.path)}
                  className="flex items-center space-x-3 p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 group text-left w-full"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors duration-200 flex-shrink-0">
                    {btn.icon}
                  </div>
                  <span className="font-medium text-gray-900 group-hover:text-blue-600 text-sm sm:text-base truncate">{btn.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6 sm:mb-8">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center">
                  <FaFilter className="text-sm" />
                </div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Analytics & Reports</h2>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-sm"
                >
                  <FaFilter className="text-sm" />
                  <span className="hidden sm:inline">{showAdvancedFilters ? "Hide" : "Show"} Advanced Filters</span>
                  <span className="sm:hidden">{showAdvancedFilters ? "Hide" : "Show"} Filters</span>
                </button>
                <button 
                  onClick={clearAllFilters}
                  className="px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 text-sm"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Site Filter</label>
                <select
                  value={selectedSite}
                  onChange={(e) => setSelectedSite(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="all">All Sites</option>
                  {sites.map((site) => (
                    <option key={site._id} value={site._id}>
                      {site.displayName || site.siteName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time Period</label>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="today">Today</option>
                  <option value="last7">Last 7 Days</option>
                  <option value="last30">Last 30 Days</option>
                  <option value="last90">Last 3 Months</option>
                  <option value="last180">Last 6 Months</option>
                  <option value="year">This Year</option>
                  <option value="all">All Time</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {filter === "custom" && (
                <>
                  <div className="sm:col-span-2 lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2 lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </>
              )}
            </div>

            {showAdvancedFilters && (
              <div className="border-t border-gray-200 pt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sales Agent</label>
                    <select
                      value={selectedAgent}
                      onChange={(e) => setSelectedAgent(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="">All Agents</option>
                      {agents.map((agent) => (
                        <option key={agent._id} value={agent.name}>
                          {agent.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Customer Type</label>
                    <select
                      value={selectedCustomerType}
                      onChange={(e) => setSelectedCustomerType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="">All Types</option>
                      <option value="Foreigner">Foreigner</option>
                      <option value="Local">Local</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Booking Type</label>
                    <select
                      value={selectedBookingType}
                      onChange={(e) => setSelectedBookingType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="">All Booking Types</option>
                      <option value="Walk-in (approach us)">Walk-in (approach us)</option>
                      <option value="Walk-in (approach by team)">Walk-in (approach by team)</option>
                      <option value="Online Booking">Online Booking</option>
                      <option value="Brought by Guide">Brought by Guide</option>
                      <option value="In-house">In-house</option>
                      <option value="Restaurant">Restaurant</option>
                      <option value="OTA">OTA</option>
                      <option value="DMC">DMC</option>
                      <option value="Other (Shops/Restaurants)">Other (Shops/Restaurants)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                    <select
                      value={selectedPaymentMethod}
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="">All Methods</option>
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-blue-800">
                <span className="font-medium">Results:</span> Showing {filteredSales.length} sales totaling{" "}
                {filteredSales.reduce((sum, sale) => sum + sale.numberOfTickets, 0)} tickets and LKR{" "}
                {filteredSales.reduce((sum, sale) => sum + sale.totalPrice, 0).toLocaleString()} revenue
              </p>
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
          {/* Revenue Trend Chart */}
          <div className="xl:col-span-2 bg-white rounded-xl shadow-lg border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Revenue Trend (Last 30 Days)</h3>
            </div>
            <div className="p-3 sm:p-6">
              <ResponsiveContainer width="100%" height={250} className="sm:h-[300px]">
                <ComposedChart data={timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    stroke="#6b7280"
                    tick={{ fontSize: 10 }}
                    className="sm:text-xs"
                    tickFormatter={(value) =>
                      new Date(value).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })
                    }
                  />
                  <YAxis yAxisId="left" stroke="#6b7280" tick={{ fontSize: 10 }} className="sm:text-xs" />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke="#6b7280"
                    tick={{ fontSize: 10 }}
                    className="sm:text-xs"
                  />
                  <Tooltip
                    formatter={(value, name) => [
                      name === "revenue" ? `LKR ${value.toLocaleString()}` : value,
                      name === "revenue"
                        ? "Revenue"
                        : name === "tickets"
                        ? "Tickets"
                        : "Sales",
                    ]}
                    labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  />
                  <Legend />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="revenue"
                    fill="#3b82f6"
                    fillOpacity={0.3}
                    stroke="#3b82f6"
                  />
                  <Bar yAxisId="right" dataKey="tickets" fill="#06b6d4" />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="sales"
                    stroke="#10b981"
                    strokeWidth={3}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Agent Performance Chart */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Top Performing Agents</h3>
            </div>
            <div className="p-3 sm:p-6">
              {getAgentPerformanceData().length > 0 ? (
                <ResponsiveContainer width="100%" height={250} className="sm:h-[300px]">
                  <BarChart
                    data={getAgentPerformanceData().slice(0, 5)}
                    layout="horizontal"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" stroke="#6b7280" tick={{ fontSize: 10 }} className="sm:text-xs" />
                    <YAxis
                      dataKey="agent"
                      type="category"
                      stroke="#6b7280"
                      tick={{ fontSize: 10 }}
                      className="sm:text-xs"
                      width={80}
                    />
                    <Tooltip
                      formatter={(value, name) => [
                        name === "revenue" ? `LKR ${value.toLocaleString()}` : value,
                        name === "revenue"
                          ? "Revenue"
                          : name === "tickets"
                          ? "Tickets"
                          : "Sales",
                      ]}
                    />
                    <Bar dataKey="revenue" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  <div className="text-center">
                    <p className="text-lg mb-2">No agent data available</p>
                    <p className="text-sm">Sales data may not include agent information</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Hourly Sales Distribution */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Sales by Hour</h3>
            </div>
            <div className="p-3 sm:p-6">
              <ResponsiveContainer width="100%" height={250} className="sm:h-[300px]">
                <AreaChart data={getHourlyDistribution()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="hour" stroke="#6b7280" tick={{ fontSize: 10 }} className="sm:text-xs" />
                  <YAxis stroke="#6b7280" tick={{ fontSize: 10 }} className="sm:text-xs" />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="sales"
                    stroke="#06b6d4"
                    fill="#06b6d4"
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Customer Type Distribution */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Customer Type Distribution</h3>
            </div>
            <div className="p-3 sm:p-6">
              <ResponsiveContainer width="100%" height={250} className="sm:h-[300px]">
                <PieChart>
                  <Pie
                    data={getPieData("customerType")}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    className="sm:outerRadius-90"
                    label={({ name, percent }) =>
                      `${name} (${(percent * 100).toFixed(1)}%)`
                    }
                    labelLine={false}
                  >
                    {getPieData("customerType").map((entry, i) => (
                      <Cell
                        key={`cell-${i}`}
                        fill={pieColors[i % pieColors.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} tickets`]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Payment Method Distribution */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Payment Methods</h3>
            </div>
            <div className="p-3 sm:p-6">
              <ResponsiveContainer width="100%" height={250} className="sm:h-[300px]">
                <PieChart>
                  <Pie
                    data={getPieData("paymentMethod")}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    className="sm:outerRadius-90"
                    label={({ name, percent }) =>
                      `${name} (${(percent * 100).toFixed(1)}%)`
                    }
                    labelLine={false}
                  >
                    {getPieData("paymentMethod").map((entry, i) => (
                      <Cell
                        key={`cell-${i}`}
                        fill={pieColors[i % pieColors.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} tickets`]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Booking Type Distribution */}
          <div className="xl:col-span-2 bg-white rounded-xl shadow-lg border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Booking Type Distribution</h3>
            </div>
            <div className="p-3 sm:p-6">
              <ResponsiveContainer width="100%" height={300} className="sm:h-[350px]">
                <BarChart data={getPieData("bookingType")}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#6b7280" 
                    tick={{ fontSize: 10 }} 
                    className="sm:text-xs"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis stroke="#6b7280" tick={{ fontSize: 10 }} className="sm:text-xs" />
                  <Tooltip formatter={(value) => [`${value} tickets`]} />
                  <Bar dataKey="value" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
